import { useMemo, useEffect, useState } from "react";

import InteractiveTooltip from "@hl/base-components/lib/InteractiveTooltip";
import { InfoCircleFill } from "@hl/base-components/lib/assets/icons.generated";
import { PriceType } from "@hl/shared-features/lib/apollo/graphql.generated";
import {
  Box,
  createStyles,
  Divider,
  Flex,
  Group,
  Stack,
  Text,
  Title,
  Center,
  Loader,
} from "@mantine/core";
import { useCollections } from "@reservoir0x/reservoir-kit-ui";
import { FixedNumber } from "ethers";
import BoxContainer from "~/../../base-components/lib/BoxContainer";
import { getCurrencySymbol } from "~/../../shared-features/lib/utils/currency";

import { useGetManageTokenCollectionQuery } from "./queries.graphql.generated";

type Params = {
  collection: NonNullable<
    ReturnType<typeof useGetManageTokenCollectionQuery>["data"]
  >["getCollection"];
  // collection: Collection;x
};

const useStyles = createStyles(() => {
  return {
    groupChild: {
      overflow: "hidden",
      flexBasis: "100%",
    },
  };
});

export default function ManageTokenStats(params: Params) {
  const { classes } = useStyles();
  const { collection: collection } = params;
  const [royaltiesEarned, setRoyaltiesEarned] = useState("0");
  // const isLoadingUniqueOwners = true;
  const { data: marketplaceData, isLoading: isLoadingMarketplaceData } =
    useCollections(
      {
        id: collection.address,
      },
      undefined,
      collection.chainId
    );

  const extractedData = useMemo(() => {
    let currencySymbol = "ETH";
    let totalSalesFN = FixedNumber.from("0");
    let totalSupply = 0;
    const totalMinted = collection?.supply ?? 0;
    const uniqueOwners = 0;

    let dutchAuctionEarningsNotFinalized = false;
    try {
      const { chainId, mintVectors, size } = collection;
      totalSupply = size ?? 0;
      currencySymbol = getCurrencySymbol(chainId);

      for (const vector of mintVectors) {
        totalSalesFN = totalSalesFN.addUnsafe(
          vector.stats?.earned
            ? FixedNumber.from(vector.stats?.earned).mulUnsafe(
                FixedNumber.from(vector.ethRate)
              )
            : FixedNumber.from("0")
        );
        // if any of the vectors is an onchain DA with an unfinalized sale, don't show earnings
        dutchAuctionEarningsNotFinalized =
          dutchAuctionEarningsNotFinalized ||
          (vector.priceType === PriceType.DutchAuction &&
            (vector.isDirectMint ?? false) &&
            vector.stats?.onchainDutchAuctionStats?.earnedAmountIsValid !=
              true &&
            (vector.stats?.sold ?? 0) > 0);
      }
    } catch (e) {
      console.error(e);
    }

    return {
      totalSupply,
      totalSales: totalSalesFN.round(4).toString(),
      currencySymbol,
      totalMinted,
      uniqueOwners,
      dutchAuctionEarningsNotFinalized,
    };
  }, [collection]);

  useEffect(() => {
    try {
      const marketplaceCollection = marketplaceData[0];
      if (
        isLoadingMarketplaceData ||
        marketplaceCollection?.volume?.allTime == null
      ) {
        return;
      }

      const royaltyPercentage = FixedNumber.from(
        collection.royalty?.royaltyPercentageString || "0"
      );
      const secondarySales = FixedNumber.from(
        marketplaceCollection.volume.allTime.toString()
      );
      setRoyaltiesEarned(
        royaltyPercentage
          .divUnsafe(FixedNumber.from("100"))
          .mulUnsafe(secondarySales)
          .round(4)
          .toString()
      );
    } catch (e) {
      setRoyaltiesEarned("0");
    }
  }, [
    marketplaceData,
    isLoadingMarketplaceData,
    collection.royalty?.royaltyPercentageString,
  ]);

  return (
    <BoxContainer p={20}>
      <Flex direction={"row"} gap={"lg"} align="start">
        <Box className={classes.groupChild}>
          <Stack spacing={4} align="center">
            <Group spacing={4}>
              <Text size={"sm"}>Total sales</Text>
              {extractedData.dutchAuctionEarningsNotFinalized && (
                <TotalSalesTooltip
                  dutchAuctionEarningsNotFinalized={
                    extractedData.dutchAuctionEarningsNotFinalized
                  }
                />
              )}
            </Group>
            <Group spacing={4} position="center" align="baseline">
              <Title order={5} align="center">
                {extractedData.totalSales}
              </Title>
              <Text size={"sm"}>{extractedData.currencySymbol}</Text>
            </Group>
          </Stack>
        </Box>
        <Divider orientation="vertical" maw={1} />
        <Box className={classes.groupChild}>
          <Stack spacing={4} align="center">
            <Group spacing={4}>
              <Text size={"sm"}>Total minted</Text>
            </Group>
            <Group spacing={4} position="center" align="baseline">
              <Title order={5} align="center">
                {extractedData.totalMinted}
              </Title>
              {extractedData.totalSupply !== 0 && (
                <Text size={"sm"}>/ {extractedData.totalSupply}</Text>
              )}
            </Group>
          </Stack>
        </Box>
        <Divider orientation="vertical" maw={1} />
        <Box className={classes.groupChild}>
          <Stack spacing={4} align="center">
            <Group spacing={4}>
              <Text size={"sm"}>Royalties earned</Text>
            </Group>
            <Group spacing={4} position="center" align="baseline">
              {isLoadingMarketplaceData ? (
                <Center>
                  <Loader />
                </Center>
              ) : (
                <>
                  <Title order={5} align="center">
                    {royaltiesEarned}
                  </Title>
                  <Text size={"sm"}>{extractedData.currencySymbol}</Text>
                </>
              )}
            </Group>
          </Stack>
        </Box>
        {/*<Divider orientation="vertical" maw={1} />*/}
        {/*<Box className={classes.groupChild}>*/}
        {/*  <Stack spacing={4} align="center">*/}
        {/*    <Group spacing={4}>*/}
        {/*      <Text size={"sm"}>Unique owners</Text>*/}
        {/*    </Group>*/}
        {/*    <Group spacing={4} position="center" align="baseline">*/}
        {/*      {isLoadingUniqueOwners ? (*/}
        {/*        <Loader aria-label="loading" size={24} mt={4} />*/}
        {/*      ) : (*/}
        {/*        <Title order={5} align="center">*/}
        {/*          100*/}
        {/*        </Title>*/}
        {/*      )}*/}
        {/*    </Group>*/}
        {/*  </Stack>*/}
        {/*</Box>*/}
      </Flex>
    </BoxContainer>
  );
}

function TotalSalesTooltip({
  dutchAuctionEarningsNotFinalized,
}: {
  dutchAuctionEarningsNotFinalized?: boolean;
}) {
  return (
    <InteractiveTooltip target={<InfoCircleFill />}>
      <Stack spacing={6} align="flex-start">
        {dutchAuctionEarningsNotFinalized && (
          <div>The public dutch auction does not have finalized earnings</div>
        )}
      </Stack>
    </InteractiveTooltip>
  );
}
